import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { CustomSnackBarComponent } from 'app/common/custom-snack-bar/custom-snack-bar.component';
import { environment } from '@environment/environment';
import { UrlService } from '@service/url/url.service';
import {
	ANNUAL_VOLUME_DISPLAY,
	CarriersProjectsList,
	GET_LANE_PATH_STRING,
	IS_NOT_EMPTY_LIST,
	IS_NOT_NULL_OR_UNDEFINED,
	ProjectCarrierMapDTO,
	ProjectDTO,
	RestService,
	TruckSearchResult,
} from 'cargo-chief-commons';
import { firstValueFrom } from 'rxjs';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { AddCarriersDialogComponent } from '@shared/add-carriers-dialog/add-carriers-dialog.component';
import { CarrierToProjectsMap } from '@domain/interface/carrier-to-projects-map';
import { Store } from '@ngrx/store';
import { State } from '@state/core.state';
import { PROJECT_CARRIER_MAP } from 'app/project/store/actions';
import { ProjectDetailsState } from 'app/project/store/state';

@Injectable({
	providedIn: 'root',
})
export class ProjectUtilService {
	constructor(
		private matSnackbar: MatSnackBar,
		private urlService: UrlService,
		private restService: RestService,
		private dialog: MatDialog,
		private readonly store: Store<State>
	) {}

	isProjectExistsError(error) {
		const errorMessage = error['errorMessage'];
		return (
			errorMessage && errorMessage.includes('Project already exists') && error['existingProjectId']
		);
	}

	showProjectExistsError({ forAdd, error }: { forAdd: boolean; error: Object }) {
		const existingProjectId = error['existingProjectId'];
		const displayMessage = `Cannot ${forAdd ? 'add' : 'update'} project as project already
            <a href='${environment.clientUrl}projects/project_details/${existingProjectId}' class='color-white text-underline'>exists</a>`;
		this.matSnackbar.openFromComponent(CustomSnackBarComponent, {
			verticalPosition: 'top',
			panelClass: SNACKBAR.ERROR,
			duration: SNACKBAR.CUSTOM_SNACKBAR_DURATION,
			data: {
				html: displayMessage,
			},
		});
	}

	getEquipmentForScorecardLane({
		originCity,
		originState,
		destinationCity,
		destinationState,
		scorecardStartDate,
		scorecardEndDate,
		scorecardLoadsCount,
		carrierId,
		customerId,
	}: {
		originCity?: string;
		originState?: string;
		destinationCity?: string;
		destinationState?: string;
		scorecardStartDate: string;
		scorecardEndDate: string;
		scorecardLoadsCount: number;
		carrierId?: string;
		customerId?: string;
	}): Promise<{ data: string }> {
		const payload = {
			originCity,
			originState,
			destinationCity,
			destinationState,
			scorecardStartDate,
			scorecardEndDate,
			scorecardLoadsCount,
			carrierId,
			customerId,
		};
		const url = this.urlService.getProjectEquipment();
		return firstValueFrom(this.restService.POST(url, payload));
	}

	showAddCarriersDialog({
		startDate,
		endDate,
		originCity,
		originState,
		destinationCity,
		destinationState,
		loadsCount,
		customerId,
		projectIdList,
	}): Promise<any> {
		const dialogRef = this.dialog.open(AddCarriersDialogComponent, {
			width: '600px',
			data: {
				scorecardStartDate: startDate,
				scorecardEndDate: endDate,
				originCity: originCity,
				originState: originState,
				destinationCity: destinationCity,
				destinationState: destinationState,
				scorecardLoadsCount: loadsCount,
				customerId: customerId,
				projectIdList: projectIdList,
				title: 'Add Carriers To Project',
			},
		});
		return dialogRef.afterClosed().toPromise();
	}

	carrierProjectMap(truckSearchResult: TruckSearchResult[], allProjects: ProjectDTO[]): {} {
		const result = {};
		truckSearchResult.map((search: TruckSearchResult, index: number) => {
			const carrierId = search.carrier.carrierHubId;
			result[carrierId] = [];
			allProjects?.map((project: ProjectDTO) => {
				let carrierProjects: Array<ProjectDTO> = allProjects.filter((project) => {
					const procuredCarrierIdList = project.procuredCarrierIdList;
					return (
						IS_NOT_EMPTY_LIST(procuredCarrierIdList) && procuredCarrierIdList.includes(carrierId)
					);
				});
				let projectMap = {} as CarrierToProjectsMap;
				projectMap.projectId = project.id;
				projectMap.carrierId = carrierId;
				projectMap.isCarrierMappedToProject = carrierProjects.length > 0;
				projectMap.projectName = project.name;
				projectMap.lane = GET_LANE_PATH_STRING(project);
				projectMap.customer = project?.customerDTO?.name;
				projectMap.isProjectNameDisplayed = projectMap.lane == project.name;
				projectMap.carrierCount = project.carrierCount;
				projectMap.type = project.type;
				if (
					IS_NOT_NULL_OR_UNDEFINED(project.loadVolume) &&
					IS_NOT_NULL_OR_UNDEFINED(project.loadVolumeDuration)
				) {
					const annualVolume = ANNUAL_VOLUME_DISPLAY(project);
					if (IS_NOT_NULL_OR_UNDEFINED(annualVolume)) {
						projectMap.monthlyVolumeDisplay = `${Math.round(annualVolume / 12)}/Month`;
					}
				}
				result[carrierId].push(projectMap);
			});
		});
		return result;
	}

	updateAccountCarrierLanes(
		projectCarrierMapList: ProjectCarrierMapDTO[],
		projectDetailsPageState: ProjectDetailsState
	) {
		const updatedCarrierLaneData = {};
		projectCarrierMapList.forEach((pcm) => {
			const search = projectDetailsPageState.capacitySearchResults.find(
				(search) => search.carrierId === pcm.carrier.id
			);
			if (search) {
				if (IS_NOT_NULL_OR_UNDEFINED(search.rateUpdatedAt)) {
					updatedCarrierLaneData[search.carrierId] = {
						rateUpdatedAt: search.rateUpdatedAt,
						rate: IS_NOT_NULL_OR_UNDEFINED(search.rate) ? search.rate : null,
					};
				}
			}
		});
		if (Object.keys(updatedCarrierLaneData).length > 0) {
			const existingCarrierLaneData = projectDetailsPageState.accountCarrierLanes;
			for (const carrierId in updatedCarrierLaneData) {
				existingCarrierLaneData[carrierId] = updatedCarrierLaneData[carrierId];
			}
			this.store.dispatch(
				PROJECT_CARRIER_MAP.LANES.ACCOUNT_CARRIER.SUCCESS({
					accountCarrierLanes: existingCarrierLaneData,
				})
			);
		}
	}

	async getCarrierProjectListData(
		carrierIds: string[]
	): Promise<{ [carrierId: string]: ProjectDTO[] }> {
		let result = {};
		const url = this.urlService.getProjectsByCarrierIdsAndAccountId();
		const payload = { carrierIds };
		const carriersProjectsListResponse = await firstValueFrom(this.restService.POST(url, payload));
		const carriersProjectsList: CarriersProjectsList[] = carriersProjectsListResponse['data'];
		carrierIds.forEach((carrierId) => {
			const carriersProjects = carriersProjectsList.find(
				(carrierToProjectMap) => carrierToProjectMap.carrierId == carrierId
			);
			if (carriersProjects) {
				result[carrierId] = carriersProjects.projectDTOList;
			}
		});
		return Promise.resolve(result);
	}
}
